import { createTheme, type ThemeOptions } from '@mui/material/styles';

declare module '@mui/material/styles' {
  interface Palette {
    brand: {
      primary: string;
      secondary: string;
      accent: string;
      highlight: string;
      success: string;
      error: string;
    };
  }
  interface PaletteOptions {
    brand?: {
      primary: string;
      secondary: string;
      accent: string;
      highlight: string;
      success: string;
      error: string;
    };
  }
}

// Minimal Clean Color System
export const minimalColors = {
  // Brand Colors (Tunisian Heritage)
  brand: {
    primary: '#273B7D',    // Deep Blue - Trust & Headers
    secondary: '#499FDD',  // Sky Blue - Hover & Links
    accent: '#FF690F',     // Bright Orange - CTAs
    highlight: '#FFD32F',  // Gold/Yellow
    success: '#5EBD81',    // Teal Green
    error: '#C8102E',      // Tunisia Flag Red
  },

  // Base Colors (95% of UI)
  background: {
    app: '#F8FAFC',
    paper: '#FFFFFF',
    sidebar: '#FFFFFF',
    tableHeader: '#F9FAFB',
    hover: '#F3F4F6',
  },

  // Borders & Dividers
  border: {
    light: '#E5E7EB',
    divider: '#EDF0F2',
    input: '#D1D5DB',
  },

  // Text
  text: {
    primary: '#111827',
    secondary: '#4B5563',
    muted: '#9CA3AF',
  },

  // Single Accent (neutral dark)
  accent: {
    primary: '#1F2937',
    hover: '#111827',
    softBg: '#F3F4F6',
  },

  // Status Colors (very muted, badge-only)
  status: {
    success: '#22C55E',
    successBg: '#ECFDF5',
    warning: '#F59E0B',
    warningBg: '#FFFBEB',
    error: '#EF4444',
    errorBg: '#FEF2F2',
    info: '#3B82F6',
    infoBg: '#EFF6FF',
    cancelled: '#EF4444',
    cancelledBg: '#FEF2F2',
  },

  // Icons
  icon: {
    default: '#6B7280',
    dark: '#374151',
    bg: '#F3F4F6',
  },
};

// Light theme options
const lightThemeOptions: ThemeOptions = {
  palette: {
    mode: 'light',
    brand: minimalColors.brand,
    primary: {
      main: minimalColors.brand.primary,
      light: minimalColors.brand.secondary,
      dark: '#1E2F5F',
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: minimalColors.text.secondary,
      light: minimalColors.border.light,
      dark: minimalColors.text.primary,
      contrastText: '#FFFFFF',
    },
    background: {
      default: minimalColors.background.app,
      paper: minimalColors.background.paper,
    },
    text: {
      primary: minimalColors.text.primary,
      secondary: minimalColors.text.secondary,
      disabled: minimalColors.text.muted,
    },
    divider: minimalColors.border.divider,
    success: {
      main: minimalColors.status.success,
      light: minimalColors.status.successBg,
      contrastText: '#FFFFFF',
    },
    warning: {
      main: minimalColors.status.warning,
      light: minimalColors.status.warningBg,
      contrastText: '#FFFFFF',
    },
    error: {
      main: minimalColors.status.error,
      light: minimalColors.status.errorBg,
      contrastText: '#FFFFFF',
    },
    info: {
      main: minimalColors.status.info,
      light: minimalColors.status.infoBg,
      contrastText: '#FFFFFF',
    },
  },
  typography: {
    fontFamily: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    h1: { fontWeight: 600, color: minimalColors.text.primary },
    h2: { fontWeight: 600, color: minimalColors.text.primary },
    h3: { fontWeight: 600, color: minimalColors.text.primary },
    h4: { fontWeight: 600, color: minimalColors.text.primary },
    h5: { fontWeight: 600, color: minimalColors.text.primary },
    h6: { fontWeight: 600, color: minimalColors.text.primary },
    body1: { color: minimalColors.text.primary },
    body2: { color: minimalColors.text.secondary },
    caption: { color: minimalColors.text.muted },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: minimalColors.background.app,
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          borderRadius: 8,
        },
        contained: {
          backgroundColor: minimalColors.accent.primary,
          color: '#FFFFFF',
          '&:hover': {
            backgroundColor: minimalColors.accent.hover,
          },
        },
        outlined: {
          backgroundColor: minimalColors.background.paper,
          borderColor: minimalColors.border.input,
          color: minimalColors.accent.primary,
          '&:hover': {
            backgroundColor: minimalColors.background.hover,
            borderColor: minimalColors.border.input,
          },
        },
        text: {
          color: minimalColors.text.secondary,
          '&:hover': {
            backgroundColor: minimalColors.background.hover,
          },
        },
      },
    },
    MuiCard: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          backgroundColor: minimalColors.background.paper,
          border: `1px solid ${minimalColors.border.light}`,
          borderRadius: 8,
        },
      },
    },
    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
        outlined: {
          borderColor: minimalColors.border.light,
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        size: 'small',
      },
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-root': {
            backgroundColor: minimalColors.background.paper,
            '& fieldset': {
              borderColor: minimalColors.border.input,
            },
            '&:hover fieldset': {
              borderColor: minimalColors.text.muted,
            },
            '&.Mui-focused fieldset': {
              borderColor: minimalColors.accent.primary,
              borderWidth: 1,
            },
          },
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
          borderRadius: 6,
        },
        filled: {
          backgroundColor: minimalColors.background.hover,
          color: minimalColors.text.primary,
        },
        outlined: {
          borderColor: minimalColors.border.light,
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          borderColor: minimalColors.border.divider,
        },
      },
    },
    MuiAppBar: {
      defaultProps: {
        elevation: 0,
      },
      styleOverrides: {
        root: {
          backgroundColor: minimalColors.background.paper,
          borderBottom: 'none',
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundColor: minimalColors.background.sidebar,
          borderRight: 'none',
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 6,
          '&.Mui-selected': {
            backgroundColor: minimalColors.background.hover,
            color: minimalColors.text.primary,
            '&:hover': {
              backgroundColor: minimalColors.background.hover,
            },
          },
          '&:hover': {
            backgroundColor: minimalColors.background.hover,
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
          color: minimalColors.text.secondary,
          '&.Mui-selected': {
            color: minimalColors.text.primary,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: minimalColors.accent.primary,
          height: 2,
        },
      },
    },
    MuiTableHead: {
      styleOverrides: {
        root: {
          backgroundColor: minimalColors.background.tableHeader,
          '& .MuiTableCell-head': {
            fontWeight: 600,
            color: minimalColors.text.secondary,
            borderBottom: `1px solid ${minimalColors.border.light}`,
          },
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': {
            backgroundColor: minimalColors.background.hover,
          },
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          borderBottom: `1px solid ${minimalColors.border.divider}`,
          color: minimalColors.text.primary,
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
        standardSuccess: {
          backgroundColor: minimalColors.status.successBg,
          color: minimalColors.status.success,
        },
        standardWarning: {
          backgroundColor: minimalColors.status.warningBg,
          color: minimalColors.status.warning,
        },
        standardError: {
          backgroundColor: minimalColors.status.errorBg,
          color: minimalColors.status.error,
        },
        standardInfo: {
          backgroundColor: minimalColors.status.infoBg,
          color: minimalColors.status.info,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: 12,
          border: `1px solid ${minimalColors.border.light}`,
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          border: `1px solid ${minimalColors.border.light}`,
          borderRadius: 8,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: minimalColors.accent.primary,
          color: '#FFFFFF',
          fontSize: '0.75rem',
        },
      },
    },
    MuiAvatar: {
      styleOverrides: {
        root: {
          backgroundColor: minimalColors.icon.bg,
          color: minimalColors.icon.dark,
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          color: minimalColors.icon.default,
          '&:hover': {
            backgroundColor: minimalColors.background.hover,
          },
        },
      },
    },
    MuiSelect: {
      styleOverrides: {
        outlined: {
          '&.MuiOutlinedInput-input': {
            backgroundColor: minimalColors.background.paper,
          },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          '&.Mui-checked': {
            color: minimalColors.accent.primary,
            '& + .MuiSwitch-track': {
              backgroundColor: minimalColors.accent.primary,
            },
          },
        },
      },
    },
  },
};

// Dark theme (minimal adjustments)
const darkThemeOptions: ThemeOptions = {
  ...lightThemeOptions,
  palette: {
    ...lightThemeOptions.palette,
    mode: 'dark',
    background: {
      default: '#0F172A',
      paper: '#1E293B',
    },
    text: {
      primary: '#F8FAFC',
      secondary: '#94A3B8',
      disabled: '#64748B',
    },
    divider: '#334155',
  },
};

export const lightTheme = createTheme(lightThemeOptions);
export const darkTheme = createTheme(darkThemeOptions);
export const colors = minimalColors;
